import React from 'react'
import { Typography, TypographyProps } from '@mui/material'
import { makeStyles } from '@mui/styles'
import classNames from 'classnames'

import Colors from '../../constants/colors'

const useStyles = makeStyles(
  theme => ({
    itemStateText: {
      fontWeight: 500,
      padding: theme.spacing(0, 0.5),
      borderRadius: 2,
      backgroundColor: theme.colors.tableLeftColumnBackground,
      color: theme.colors.title,
    },
    itemInline: {
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
    },
    itemStateTextSmall: {
      fontSize: '1rem',
    },
    itemStateTextBig: {
      fontSize: '1.6rem',
    },
    normalState: {},
    warningState: {
      backgroundColor: theme.colors.actionNeededBackground,
      color: theme.colors.important,
    },
    disabledState: {
      backgroundColor: theme.colors.disabledLabel,
      color: theme.colors.disabledLabelText,
    },
    completedState: {
      backgroundColor: theme.colors.success,
      color: theme.colors.completedTextColor,
    },
    errorState: {
      backgroundColor: theme.colors.errorColor,
      color: theme.colors.profileText,
    },
    successState: {
      backgroundColor: theme.colors.successBackground,
      color: theme.colors.success,
    },
    deletedState: {
      backgroundColor: Colors.RED100,
      color: Colors.ALERT,
    },
  }),
  { name: 'StateLabel' },
)

type StateLabelVariantType = 'default' | 'small' | 'big'

export interface StateLabelProps extends Omit<TypographyProps, 'variant'> {
  readonly completed?: boolean
  readonly deleted?: boolean
  readonly disabled?: boolean
  readonly error?: boolean
  readonly important?: boolean
  readonly inline?: boolean
  readonly normal?: boolean
  readonly success?: boolean
  readonly variant?: StateLabelVariantType
  readonly warning?: boolean
}

const StateLabel = ({
  children,
  normal = true,
  success = false,
  warning = false,
  important = false, // alias for warning
  disabled = false,
  completed = false,
  error = false,
  deleted = false,
  className,
  variant = 'default',
  inline = false,
  ...rest
}: StateLabelProps) => {
  const classes = useStyles(rest)

  return (
    <Typography
      className={classNames(className, classes.itemStateText, {
        [classes.normalState]: normal,
        [classes.successState]: success,
        [classes.warningState]: warning || important,
        [classes.disabledState]: disabled,
        [classes.completedState]: completed,
        [classes.errorState]: error,
        [classes.deletedState]: deleted,
        [classes.itemStateTextSmall]: variant === 'small',
        [classes.itemStateTextBig]: variant === 'big',
        [classes.itemInline]: inline,
      })}
      {...rest}
    >
      {children}
    </Typography>
  )
}

export default StateLabel
